// Google OAuth Service

interface GoogleOAuthConfig {
  clientId: string;
  redirectUri: string;
  scope: string;
}

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

export class GoogleOAuthService {
  private config: GoogleOAuthConfig;

  constructor(config: GoogleOAuthConfig) {
    this.config = config;
  }

  // Generate OAuth URL and redirect to Google
  initiateOAuth(): void {
    const state = this.generateState();
    sessionStorage.setItem('oauth_state', state);

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scope,
      state,
      access_type: 'offline',
      prompt: 'consent',
    });

    const authUrl = `${GOOGLE_AUTH_URL}?${params.toString()}`;
    window.location.href = authUrl;
  }

  // Handle OAuth callback
  async handleCallback(code: string, state: string): Promise<{
    success: boolean;
    token?: string;
    user?: {
      id: number;
      email: string;
      name: string;
      picture: string;
    };
    error?: string;
  }> {
    // Verify state
    const storedState = sessionStorage.getItem('oauth_state');
    if (state !== storedState) {
      return { success: false, error: 'Invalid state parameter' };
    }

    // Clear state
    sessionStorage.removeItem('oauth_state');

    // Exchange code for tokens via backend
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${apiUrl}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          redirect_uri: this.config.redirectUri,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'OAuth failed' };
      }

      const data = await response.json();

      // Store token and user info
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      return {
        success: true,
        token: data.token,
        user: data.user,
      };
    } catch (error) {
      console.error('OAuth callback error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  // Generate random state for CSRF protection
  private generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  // Check if user is authenticated
  static isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  // Get current user
  static getCurrentUser(): any | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Logout
  static logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }
}

// Create default instance
export const googleOAuth = new GoogleOAuthService({
  clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
  redirectUri: import.meta.env.VITE_GOOGLE_REDIRECT_URI || 'http://localhost:3000/auth/callback',
  scope: 'openid email profile',
});
